import React, { useEffect, useState } from "react";
import { Box, Drawer, Button, IconButton, TextField, Tooltip } from "@mui/material";
import { IconX, IconPencil, IconDownload } from "@tabler/icons-react";
import tokens from "../../theme/tokens";
import { getBestScreeningResult } from "../../../services/screeningService";
import { getInitials, formatScore } from "./candidateRow";
import ScreeningAttempt from "./ScreeningAttempt";
import { screenProgress, screenOutcome } from "./screeningStatus";
import { exportCandidate } from "./candidateExport";
import { isInProgress } from "./resumeStatus";

const { color, font, layout, radius, shadow, scoreColor } = tokens;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SectionTitle = ({ children }) => (
  <Box
    sx={{
      mt: 3,
      mb: 1.25,
      font: `600 11px ${font.sans}`,
      color: color.textMuted,
      letterSpacing: ".07em",
    }}
  >
    {children}
  </Box>
);

const Chip = ({ bg, fg, children }) => (
  <Box
    component="span"
    sx={{
      font: `600 10px ${font.sans}`,
      textTransform: "uppercase",
      borderRadius: `${radius.chip}px`,
      px: 0.875,
      py: 0.375,
      bgcolor: bg,
      color: fg,
    }}
  >
    {children}
  </Box>
);

/**
 * Replaces ScreeningDetailDialog. Shows the latest screening attempt inline and any
 * earlier attempts below it. The old dialog linked the recording as a bare anchor —
 * this embeds a real player so a recruiter can listen without leaving the console.
 */
const CandidateDetailPanel = ({
  open,
  onClose,
  row,
  jobId,
  screening,
  interview,
  onMoveToScreening,
  onSaveEmail,
  onSavePhone,
}) => {
  // Hooks must run unconditionally — declared above the `!row` bail-out below.
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailError, setEmailError] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  // Reset the inline editors whenever a different candidate is opened, otherwise the panel
  // reopens still in edit mode holding the previous candidate's details.
  useEffect(() => {
    setEditingEmail(false);
    setEmailError("");
    setEditingPhone(false);
    setPhoneError("");
  }, [row?.resumeId]);

  if (!row) return null;

  const results = screening?.results || [];

  // Fixing a wrong/missing email matters: interview invites go out by email, so a bad one
  // means the candidate silently never hears from you. The old Report table had this;
  // the console didn't.
  const saveEmail = async () => {
    if (!EMAIL.test(emailDraft.trim())) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setSavingEmail(true);
    try {
      await onSaveEmail(row.resumeId, emailDraft.trim());
      setEditingEmail(false);
    } catch (e) {
      setEmailError(e.message || "Couldn't update the email.");
    } finally {
      setSavingEmail(false);
    }
  };

  // Phone is stored on the resume's candidate_info, so this saves through onSavePhone
  // (the resume endpoint), separate from the email path above.
  const savePhone = async () => {
    if (!/\d/.test(phoneDraft)) {
      setPhoneError("Enter a phone number.");
      return;
    }
    setSavingPhone(true);
    try {
      await onSavePhone(row.resumeId, phoneDraft.trim());
      setEditingPhone(false);
    } catch (e) {
      setPhoneError(e.message || "Couldn't update the phone.");
    } finally {
      setSavingPhone(false);
    }
  };
  // The headline chip shows the candidate's BEST completed attempt, not the most
  // recent — a later no-answer doesn't erase a good call. The per-attempt list below
  // still shows every attempt in order.
  const best = getBestScreeningResult(results);
  // Two facts, two chips — how far the call got, and (only once it finished) how it went. Same
  // vocabulary and colours as the table's SCREEN column; see screeningStatus.js.
  const progress = screenProgress(screening);
  const outcome = screenOutcome(screening);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: layout.detailPanel,
          maxWidth: "100%",
          boxShadow: shadow.panel,
          borderLeft: `1px solid ${color.border}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2.75, pb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            flex: "none",
            display: "grid",
            placeItems: "center",
            borderRadius: `${radius.fileRow}px`,
            bgcolor: color.indigoTint,
            color: color.indigo,
            font: `600 13px ${font.sans}`,
          }}
        >
          {getInitials(row.name)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ font: `600 15px ${font.sans}`, color: color.text }}>{row.name}</Box>
          <Box
            sx={{
              mt: 0.25,
              font: `400 11.5px ${font.sans}`,
              color: color.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {editingEmail ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                <TextField
                  size="small"
                  autoFocus
                  value={emailDraft}
                  onChange={(e) => {
                    setEmailDraft(e.target.value);
                    setEmailError("");
                  }}
                  error={Boolean(emailError)}
                  helperText={emailError}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-input": { fontSize: 12, py: 0.75 },
                  }}
                />
                <Button size="small" variant="contained" onClick={saveEmail} disabled={savingEmail}>
                  Save
                </Button>
                <Button size="small" onClick={() => setEditingEmail(false)}>
                  Cancel
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    // Flag a missing email: interview invites are sent by email, so a
                    // candidate without one silently drops out of the flow.
                    color: row.email ? color.textMuted : color.danger,
                  }}
                >
                  {row.email || "No email"}
                </Box>
                {onSaveEmail && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEmailDraft(row.email || "");
                      setEmailError("");
                      setEditingEmail(true);
                    }}
                    aria-label="Edit email"
                  >
                    <IconPencil size={13} stroke={1.75} color={color.textMuted} />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
          {/* Phone — same inline-edit pattern as email, saved via onSavePhone (resume endpoint). */}
          <Box
            sx={{
              mt: 0.25,
              font: `400 11.5px ${font.sans}`,
              color: color.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {editingPhone ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                <TextField
                  size="small"
                  autoFocus
                  value={phoneDraft}
                  onChange={(e) => {
                    setPhoneDraft(e.target.value);
                    setPhoneError("");
                  }}
                  error={Boolean(phoneError)}
                  helperText={phoneError}
                  sx={{ flex: 1, "& .MuiOutlinedInput-input": { fontSize: 12, py: 0.75 } }}
                />
                <Button size="small" variant="contained" onClick={savePhone} disabled={savingPhone}>
                  Save
                </Button>
                <Button size="small" onClick={() => setEditingPhone(false)}>
                  Cancel
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row.phone || "No phone"}
                </Box>
                {onSavePhone && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPhoneDraft(row.phone || "");
                      setPhoneError("");
                      setEditingPhone(true);
                    }}
                    aria-label="Edit phone"
                  >
                    <IconPencil size={13} stroke={1.75} color={color.textMuted} />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </Box>
        {/* Everything this panel knows about the candidate — resume scores and every call
            attempt, transcripts included — as one flat file. See candidateExport.js. */}
        <IconButton
          size="small"
          onClick={() => exportCandidate(row, results)}
          title="Export this candidate as CSV"
          aria-label="Export this candidate as CSV"
        >
          <IconDownload size={16} stroke={1.75} color={color.textMuted} />
        </IconButton>
        <IconButton size="small" onClick={onClose}>
          <IconX size={16} stroke={1.75} color={color.textMuted} />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.75, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        <Chip bg={progress.bg} fg={progress.fg}>{progress.label}</Chip>
        {outcome && <Chip bg={outcome.bg} fg={outcome.fg}>{outcome.label}</Chip>}
        <Chip bg={color.borderRow} fg={color.textSecondary}>
          Rank {String(row.rank ?? "").padStart(2, "0")}
        </Chip>
        <Chip bg={color.indigoTint} fg={color.indigo}>
          Total {formatScore(row.total)}
        </Chip>
      </Box>

      {/* The screen score earns its own row: it is the only number here that came from talking to
          the human being, and as a grey chip in the pill soup it read like metadata. Colour-coded
          on the same band scale as every other score — a 4.2 should look like a 4.2 everywhere. */}
      {best?.interview_rating != null && (
        <Box
          sx={{
            mx: 2.75,
            mt: 1.25,
            px: 1.5,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: `1px solid ${color.border}`,
            borderRadius: `${radius.card}px`,
            bgcolor: color.surface,
          }}
        >
          <Box>
            <Box sx={{ font: `600 11px ${font.sans}`, color: color.textSecondary, letterSpacing: ".05em" }}>
              SCREEN SCORE
            </Box>
            <Box sx={{ mt: 0.25, font: `400 11px ${font.sans}`, color: color.textMuted }}>
              Best completed call
            </Box>
          </Box>
          <Box sx={{ font: `600 26px ${font.mono}`, color: scoreColor(best.interview_rating) }}>
            {formatScore(best.interview_rating)}
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: "auto", px: 2.75, pb: 2 }}>
        {/* Named for its source: these four come from parsing the RESUME, not from the call. The
            screen score above is the call's. Two different instruments, two different numbers. */}
        <SectionTitle>RESUME SCORE BREAKDOWN</SectionTitle>
        {[
          ["Skills", row.skills],
          ["Experience", row.exp],
          ["Education", row.edu],
          ["Peer-to-peer", row.p2p],
        ].map(([label, value]) => (
          <Box
            key={label}
            sx={{
              display: "grid",
              gridTemplateColumns: "86px 1fr 32px",
              alignItems: "center",
              gap: 1.25,
              mb: 1,
            }}
          >
            <Box sx={{ font: `400 12px ${font.sans}`, color: color.textSecondary }}>
              {label}
            </Box>
            <Box sx={{ height: 5, borderRadius: 3, bgcolor: color.borderRow }}>
              <Box
                sx={{
                  height: 5,
                  borderRadius: 3,
                  width: `${Math.min(100, ((value ?? 0) / 10) * 100)}%`,
                  // Bar carries the same band colour as the number beside it, so the
                  // breakdown reads at a glance without checking each value.
                  bgcolor: scoreColor(value),
                }}
              />
            </Box>
            <Box
              sx={{
                textAlign: "right",
                font: `500 12px ${font.mono}`,
                color: scoreColor(value),
              }}
            >
              {formatScore(value)}
            </Box>
          </Box>
        ))}

        {row.interviewSelected && (
          <>
            <SectionTitle>INTERVIEW</SectionTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
                borderBottom: `1px solid ${color.borderRow}`,
              }}
            >
              <Box sx={{ font: `400 12px ${font.sans}`, color: color.textSecondary }}>Status</Box>
              <Box sx={{ font: `500 12px ${font.mono}`, color: color.text }}>
                {interview?.["Interview Status"] || "Not invited"}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
                borderBottom: `1px solid ${color.borderRow}`,
              }}
            >
              <Box sx={{ font: `400 12px ${font.sans}`, color: color.textSecondary }}>
                Interview score
              </Box>
              <Box
                sx={{
                  font: `500 12px ${font.mono}`,
                  color: scoreColor(interview?.["Total Interview score"]),
                }}
              >
                {formatScore(interview?.["Total Interview score"])}
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              {/* The candidate-facing slot picker and AI chat. Same URLs the old
                  Interview dashboard linked to. */}
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open(`/${jobId}/${row.resumeId}`, "_blank", "noopener")}
              >
                Slot picker
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open(`/${jobId}/${row.resumeId}/chat`, "_blank", "noopener")}
              >
                Interview chat
              </Button>
            </Box>
          </>
        )}

        {!screening?.status && (
          <Box sx={{ mt: 3, font: `400 12.5px ${font.sans}`, color: color.textMuted }}>
            No screening call has been scheduled for this candidate yet.
          </Box>
        )}

        {/* Every attempt, not just the latest — a candidate is often called more than
            once, and why an earlier call failed (no answer, callback asked for) is
            exactly what a recruiter needs before dialling again. The newest is
            expanded; the rest open on click. */}
        {results.length > 0 && (
          <>
            <SectionTitle>
              {results.length === 1
                ? "SCREENING CALL"
                : `SCREENING CALLS · ${results.length} ATTEMPTS`}
            </SectionTitle>
            {results.map((r, i) => (
              <ScreeningAttempt
                key={r.created_at || i}
                result={r}
                index={i}
                total={results.length}
                isBest={results.length > 1 && r === best}
                defaultOpen={i === 0}
              />
            ))}
          </>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 2.75,
          py: 2,
          borderTop: `1px solid ${color.border}`,
          bgcolor: color.bg,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => window.open(`/v1/candidate/${jobId}/${row.resumeId}`, "_blank", "noopener")}
        >
          Full profile
        </Button>
        {(() => {
          const screeningReason = row.screeningScheduled
            ? undefined
            : isInProgress(row)
            ? "Resume is still being scored"
            : !row.phone
            ? "No phone number on this candidate"
            : undefined;
          const button = (
            <Button
              variant="contained"
              disabled={row.screeningScheduled || !row.phone || isInProgress(row)}
              onClick={() => onMoveToScreening(row)}
            >
              {row.screeningScheduled ? "In screening" : "Move to screening →"}
            </Button>
          );
          // A disabled MUI Button swallows pointer events, so a title on it never fires a
          // tooltip. Put the reason on a hoverable wrapper span instead.
          return screeningReason ? (
            <Tooltip title={screeningReason} arrow>
              <span>{button}</span>
            </Tooltip>
          ) : (
            button
          );
        })()}
      </Box>
    </Drawer>
  );
};

export default CandidateDetailPanel;
